import React from 'react';
import PropTypes from 'prop-types';
import { FieldLabel, createUsePuck } from '@puckeditor/core';
import { Container } from 'reactstrap';

import { withLayout } from './PuckLayout';
import formatIntl from './Common/formatIntl';
import ColorPickerField from './Common/ColorPickerField';
import RadioField from './Common/RadioField';
import ClearableNumberInput from './Common/ClearableNumberInput';

const usePuckSelector = createUsePuck();

const resolveLinePosition = (position) => {
  if (position === 'top') {
    return { top: 0 };
  }

  if (position === 'bottom') {
    return { bottom: 0 };
  }

  return { top: '50%', transform: 'translateY(-50%)' };
};

const BorderColorField = ({ value, onChange, field }) => {
  const selectedItem = usePuckSelector((s) => s.selectedItem);
  const borderEnabled = selectedItem?.props?.border === 'enabled';

  if (!borderEnabled) {
    return <div className="puck-divider-border-color-hidden" />;
  }

  return (
    <FieldLabel label={field.label} className="puck-divider-border-color-field-label">
      <ColorPickerField
        value={value || '#dfe3ea'}
        onChange={onChange}
        showLabel={false}
        className="puck-divider-border-color-row"
        controlsClassName="puck-divider-border-color-controls"
        buttonClassName="puck-divider-border-color-btn"
        inputClassName="puck-divider-border-color-input"
        popoverClassName="puck-divider-border-color-picker-popover"
        defaultColor="#dfe3ea"
      />
    </FieldLabel>
  );
};

BorderColorField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  field: PropTypes.shape({
    label: PropTypes.string,
  }).isRequired,
};

BorderColorField.defaultProps = {
  value: '#dfe3ea',
};

const BorderPositionField = ({
  id,
  name,
  value,
  onChange,
  field,
  readOnly,
}) => {
  const selectedItem = usePuckSelector((s) => s.selectedItem);
  const borderEnabled = selectedItem?.props?.border === 'enabled';

  if (!borderEnabled) {
    return <div className="puck-divider-border-position-hidden" />;
  }

  return (
    <RadioField
      id={id}
      name={name}
      label={field.label}
      value={value || 'middle'}
      onChange={onChange}
      options={field.options}
      readOnly={readOnly}
    />
  );
};

BorderPositionField.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  field: PropTypes.shape({
    label: PropTypes.string,
    options: PropTypes.array,
  }).isRequired,
};

BorderPositionField.defaultProps = {
  id: '',
  name: '',
  value: 'middle',
  readOnly: false,
};

const BorderWidthField = ({ value, onChange, field }) => {
  const selectedItem = usePuckSelector((s) => s.selectedItem);
  const borderEnabled = selectedItem?.props?.border === 'enabled';

  if (!borderEnabled) {
    return <div className="puck-divider-border-width-hidden" />;
  }

  return (
    <ClearableNumberInput
      value={value}
      onChange={onChange}
      label={field.label}
      min={1}
      max={24}
      step={1}
    />
  );
};

BorderWidthField.propTypes = {
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  field: PropTypes.shape({
    label: PropTypes.string,
  }).isRequired,
};

BorderWidthField.defaultProps = {
  value: 1,
};

const WidthField = ({
  id,
  name,
  value,
  onChange,
  field,
  readOnly,
}) => {
  const selectedItem = usePuckSelector((s) => s.selectedItem);
  const borderEnabled = selectedItem?.props?.border === 'enabled';

  if (!borderEnabled) {
    return <div className="puck-divider-width-hidden" />;
  }

  return (
    <RadioField
      id={id}
      name={name}
      label={field.label}
      value={value || 'container'}
      onChange={onChange}
      options={field.options}
      readOnly={readOnly}
    />
  );
};

WidthField.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  field: PropTypes.shape({
    label: PropTypes.string,
    options: PropTypes.array,
  }).isRequired,
};

WidthField.defaultProps = {
  id: '',
  name: '',
  value: 'container',
  readOnly: false,
};

const PuckDividerRender = ({
  height,
  container,
  border,
  borderWidth,
  borderColor,
  borderPosition,
}) => {
  const resolvedHeight = Number.isFinite(Number(height)) ? Number(height) : 50;
  const borderEnabled = border === 'enabled';
  const resolvedBorderWidth = Number.isFinite(Number(borderWidth)) && Number(borderWidth) > 0
    ? Number(borderWidth)
    : 1;
  const linePositionStyle = resolveLinePosition(borderPosition);

  const dividerContent = (
    <div
      className="custom-page-section puck-divider"
      style={{
        minHeight: `${Math.max(0, resolvedHeight)}px`,
        position: 'relative',
      }}
    >
      {borderEnabled && (
        <div
          className="puck-divider-line"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            borderTop: `${resolvedBorderWidth}px solid ${borderColor || '#dfe3ea'}`,
            ...linePositionStyle,
          }}
        />
      )}
    </div>
  );

  if (container === 'container') {
    return <Container>{dividerContent}</Container>;
  }

  return dividerContent;
};

PuckDividerRender.propTypes = {
  height: PropTypes.number,
  container: PropTypes.oneOf(['container', 'full']),
  border: PropTypes.oneOf(['enabled', 'disabled']),
  borderWidth: PropTypes.number,
  borderColor: PropTypes.string,
  borderPosition: PropTypes.oneOf(['top', 'middle', 'bottom']),
};

PuckDividerRender.defaultProps = {
  height: 50,
  container: 'full',
  border: 'disabled',
  borderWidth: 1,
  borderColor: '#dfe3ea',
  borderPosition: 'middle',
};

const PuckDivider = {
  get label() { return formatIntl('Divider'); },

  fields: {
    height: {
      type: 'custom',
      get label() { return formatIntl('Height'); },
      render: ({ value, onChange, field }) => (
        <ClearableNumberInput
          value={value}
          onChange={onChange}
          label={field.label}
          min={0}
          max={1000}
          step={1}
        />
      ),
    },
    border: {
      type: 'radio',
      get label() { return formatIntl('Border'); },
      options: [
        { get label() { return formatIntl('Enable'); }, value: 'enabled' },
        { get label() { return formatIntl('Disable'); }, value: 'disabled' },
      ],
    },
    container: {
      type: 'custom',
      get label() { return formatIntl('Width'); },
      options: [
        { get label() { return formatIntl('Container'); }, value: 'container' },
        { get label() { return formatIntl('Full width'); }, value: 'full' },
      ],
      render: (props) => <WidthField {...props} />,
    },
    borderWidth: {
      type: 'custom',
      get label() { return formatIntl('Border width'); },
      render: (props) => <BorderWidthField {...props} />,
    },
    borderColor: {
      type: 'custom',
      get label() { return formatIntl('Border color'); },
      render: (props) => <BorderColorField {...props} />,
    },
    borderPosition: {
      type: 'custom',
      get label() { return formatIntl('Border position'); },
      options: [
        { get label() { return formatIntl('Top'); }, value: 'top' },
        { get label() { return formatIntl('Middle'); }, value: 'middle' },
        { get label() { return formatIntl('Bottom'); }, value: 'bottom' },
      ],
      render: (props) => <BorderPositionField {...props} />,
    },
  },

  defaultProps: {
    height: 50,
    container: 'full',
    border: 'disabled',
    borderWidth: 1,
    borderColor: '#dfe3ea',
    borderPosition: 'middle',
  },

  render: (props) => <PuckDividerRender {...props} />,
};

export default withLayout(PuckDivider);
